// src/lib/services/data/deltaOrdering.ts
//
// Per-entity ordering guard + live-generation epoch for the realtime data path.
//
// WHY: realtime alert/work-order deltas were applied with NO ordering guard, so
// an out-of-order or replayed message could apply an OLDER state after a NEWER
// one — e.g. an `update` arriving after a `remove` would RESURRECT a deleted
// row ("shows up then disappears", and vice-versa). This module rejects a delta
// whose envelope `sequence` is older than the newest one already applied for
// that entity id, and tombstones removes so a late lower-seq update can't undo
// them.
//
// ORDERING KEY: the backend stamps a global, monotonically-increasing envelope
// `sequence` (string int) on EVERY realtime event via _prepare_event. It is
// monotonic only within a SINGLE backend-instance lifetime — a backend RESTART
// rewinds the counter to 1. We therefore RESET all per-entity state on WS
// reconnect (resetOrdering, wired to `websocket:reconnected`), otherwise fresh
// post-restart deltas (low seq) would be wrongly rejected against a stale-high
// lastSeq. Multi-instance global ordering (no Redis pub/sub yet) is out of scope
// here and tracked separately.
//
// liveGeneration: a FE-local monotonic counter bumped whenever a LIVE WebSocket
// write is applied (delta/tick/snapshot). The REST fallback poller captures it
// before its request and drops the result if it changed — so a slow, stale REST
// response can never clobber fresher live data that landed in the meantime.

import { browser } from '$app/environment';
import { eventBus } from '$lib/core/services/EventBusService';
import { createLogger } from '$lib/utils/logger';

const log = createLogger('deltaOrdering');

export type OrderedEntity = 'alerts' | 'work_orders';

interface SeqEntry {
	seq: number;
	touchedAt: number;
	/** True if the last applied op for this id was a remove (informational). */
	tombstone: boolean;
}

const maps: Record<OrderedEntity, Map<string, SeqEntry>> = {
	alerts: new Map(),
	work_orders: new Map()
};

// Prune entries untouched for longer than this. Sized well beyond the realistic
// WS reordering window: REST timeout 30s, reconnect budget 30s, staleness 45s,
// fallback poll 30-120s. 15 min leaves a wide safety margin while bounding the
// map to active entities.
const PRUNE_TTL_MS = 15 * 60 * 1000;
let lastPruneAt = 0;

let _liveGeneration = 0;

/** Current live-write epoch. REST captures this before its request. */
export function liveGeneration(): number {
	return _liveGeneration;
}

/** Advance the live epoch — call once per applied LIVE WS write batch. */
export function bumpLiveGeneration(): void {
	_liveGeneration++;
}

function isValidSeq(seq: number | undefined): seq is number {
	return typeof seq === 'number' && Number.isSafeInteger(seq) && seq > 0;
}

/**
 * Parse a wire `sequence` (string int) to a usable number, or undefined when
 * absent/invalid (legacy backend, REST, local optimistic — those APPLY but are
 * never recorded into the ordering maps).
 */
export function parseSequence(raw: unknown): number | undefined {
	if (raw == null) return undefined;
	const n = Number(raw);
	return isValidSeq(n) ? n : undefined;
}

/**
 * Should a delta be applied given prior per-entity ordering?
 *  - no id / no valid seq        → APPLY (legacy/local/no-seq; caller must NOT record)
 *  - no prior entry for id       → APPLY
 *  - seq >= lastSeq[id]          → APPLY (>= tolerates duplicate redelivery; batches are
 *                                   coalesced by id upstream so equal-seq same-id can't race)
 *  - seq <  lastSeq[id]          → REJECT (stale: an out-of-order op after a newer one)
 */
export function shouldApply(
	entity: OrderedEntity,
	id: string | undefined,
	seq: number | undefined
): boolean {
	if (id == null || !isValidSeq(seq)) return true;
	const entry = maps[entity].get(id);
	if (!entry) return true;
	return seq >= entry.seq;
}

/**
 * Record an APPLIED delta's sequence. No-op when id/seq are absent/invalid, so
 * legacy/local optimistic writes never advance the floor. Never regresses the
 * stored max. A remove marks a tombstone (informational; rejection is seq-based).
 */
export function record(
	entity: OrderedEntity,
	id: string | undefined,
	seq: number | undefined,
	isRemove: boolean
): void {
	if (id == null || !isValidSeq(seq)) return;
	const map = maps[entity];
	const existing = map.get(id);
	if (existing && seq < existing.seq) return; // never regress
	map.set(id, { seq, touchedAt: nowMs(), tombstone: isRemove });
	maybePrune();
}

function nowMs(): number {
	return Date.now();
}

function maybePrune(): void {
	const t = nowMs();
	if (t - lastPruneAt < 60_000) return; // at most once a minute
	lastPruneAt = t;
	for (const map of Object.values(maps)) {
		for (const [id, entry] of map) {
			if (t - entry.touchedAt > PRUNE_TTL_MS) map.delete(id);
		}
	}
}

/**
 * Clear all per-entity ordering state. Called on WS reconnect (the backend
 * sequence counter may have rewound after a restart) and on logout. Also bumps
 * liveGeneration so any REST request captured before the reset is invalidated.
 */
export function resetOrdering(): void {
	maps.alerts.clear();
	maps.work_orders.clear();
	lastPruneAt = 0;
	_liveGeneration++;
	log.debug('ordering state reset (new epoch)');
}

let reconnectSubscribed = false;

/**
 * Idempotently subscribe the reconnect reset. Safe to call multiple times.
 * `websocket:reconnected` fires at transport-open, before post-reconnect
 * messages dispatch, so the maps are clean before any fresh delta arrives.
 */
export function initDeltaOrdering(): void {
	if (!browser || reconnectSubscribed) return;
	reconnectSubscribed = true;
	eventBus.on('websocket:reconnected', () => resetOrdering());
	log.debug('delta ordering initialized (reset-on-reconnect armed)');
}

/** Test-only: wipe everything including liveGeneration back to 0. */
export function __resetForTests(): void {
	maps.alerts.clear();
	maps.work_orders.clear();
	lastPruneAt = 0;
	_liveGeneration = 0;
	reconnectSubscribed = false;
}
